/**
 * OmniLeagues - Technology Specialist Settings Panel
 * Owns everything "technical" for the org: WP Bridge connection (URL + key + health),
 * API interfaces / webhooks, publish triggers. Deliberately excludes all business
 * settings (teams, pricing, refunds, coaches, registration) — those belong to the AD.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Globe, Wifi, Loader2, AlertTriangle, RefreshCw, Trash2, ExternalLink,
  KeyRound, ServerCog, Send, MonitorCheck, Link2 as LinkIcon, Copy, CheckCircle2,
  Sparkles, ArrowRight, ArrowLeft, Eye, EyeOff, Activity,
  FileText, Newspaper, Camera, Menu as MenuIcon, LayoutTemplate, Trophy,
  BadgeCheck, Tags, MessageSquare, CornerDownRight, Users, Bell,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Progress } from './ui/progress';
import ApiInterfacesPanel from './ApiInterfacesPanel';
import OrgProfilePanel from './org/OrgProfilePanel';
import { PublishTriggersSection, GamesPublishFlagSection, WpMgmtDefaultsSection } from './AdminSettingsPanel';
import WebsiteSettingsSection from './WebsiteSettingsSection';
import PopupNotificationsManager from './admin/PopupNotificationsManager';
import WebsiteVisibilityPanel from './WebsiteVisibilityPanel';
import WebsiteContentPanel from './WebsiteContentPanel';
import WebsiteMenusPanel from './WebsiteMenusPanel';
import SportLandingsPanel from './SportLandingsPanel';
import ConversionRedirectsPanel from './ConversionRedirectsPanel';
import SiteConfigPanel from './SiteConfigPanel';
import TaxonomiesPanel from './TaxonomiesPanel';
import CommentsModerationPanel from './CommentsModerationPanel';
import SyncQueueSection from './tech/SyncQueueSection';
import PlayerMergeSection from './tech/PlayerMergeSection';
import StaffRolesPanel from './tech/StaffRolesPanel';
import TeamPhotosManager from './media/TeamPhotosManager';
import HeroCarouselManager from './media/HeroCarouselManager';

const API = process.env.REACT_APP_BACKEND_URL;

// Tab definitions, grouped into the three buckets a Tech Specialist actually
// thinks about: how the OL/WP connection is wired, what content lives on the
// site, and how the site looks/behaves. Each entry: { key, label, icon, desc,
// help } where `help` is a one-paragraph "how this works" hint shown above
// the panel body.
const SECTIONS = [
  // ── CONNECTION ────────────────────────────────────────────────────
  { group: 'connection', key: 'linking', label: 'Site Linking', icon: LinkIcon,
    desc: 'Your OmniLeagues Org UUID, OL Secret Key, and live WordPress connection health — everything needed to link a WP site to this organisation.',
    help: 'OL drives WordPress through a custom plugin (ol-bridge). This tab is where you confirm the WP site URL + API key, see the bridge version that\'s running on the WP side, and verify the connection is "Connected". If this tab shows red, nothing else in Tech Settings will work — fix this first.' },
  { group: 'connection', key: 'org_contact', label: 'Org Contact & Web', icon: Globe,
    desc: 'Public email, phone, website, and social links the WP site renders. The Athletic Director owns identity and address; the Tech Specialist owns these web fields.',
    help: 'These fields are written into OL Mongo and pulled by the WordPress theme on every page load (header, footer, contact page). Update once here — the change is live on the public site within a minute.' },
  { group: 'connection', key: 'api', label: 'API Interfaces', icon: Globe,
    desc: 'MSTW legacy sync, data exports, and inbound/outbound API hooks.',
    help: 'Read-only inventory of every endpoint OL exposes (for partners, exports, scoreboards) plus the bridge endpoints OL consumes. Use this when troubleshooting integrations or onboarding a new third-party.' },
  { group: 'connection', key: 'triggers', label: 'Publish Triggers', icon: Send,
    desc: 'When OmniLeagues pushes updates to the public WP site.',
    help: 'Choose between live (every edit pushes immediately), batched (every N minutes), or manual (a Finalize button on each surface). Manual is safest during data-migration windows; live is the production default.' },
  { group: 'connection', key: 'sync', label: 'Sync Queue', icon: Activity,
    desc: 'Live visibility into the OL → WordPress replication queue: pending writes, recent errors (auto-retry), and dead letters that need manual review.',
    help: 'When OL writes to a WP-bound surface (page, post, menu, etc.) and the bridge can\'t be reached, the change is held here and retried automatically. If a row stays "error" for more than a few minutes, click it to see the bridge error message.' },
  { group: 'connection', key: 'player_merge', label: 'Player Merge', icon: KeyRound,    desc: 'AD-confirmed merging of same-name player records across seasons. Same-season duplicates are different teenagers by league rule and are never merged.',
    help: 'WP synced a roster that contains "John Smith" while OL already has a "John Smith" from last season. This panel lets the AD/Tech confirm whether they\'re the same teenager (merge) or two different ones (leave alone). Same-season same-name pairs are flagged but never auto-merged — homeschool leagues frequently have unrelated kids with identical names.' },
  { group: 'connection', key: 'staff_roles', label: 'Staff Roles', icon: Users,
    desc: 'The editable list of staff roles (Head Coach, Assistant Coach, President, Tech Specialist…) and organization areas that power the Staff Positions tab and every canonical position slug.',
    help: 'Every staff position is built from a role code + a team level (or an org area). This is the single source for that list — rename a label and it updates everywhere; add a role and it appears in the Staff Positions form. Short codes are fixed because they live inside the slug. A role in use can\'t be deleted (deactivate it instead).' },

  // ── CONTENT ───────────────────────────────────────────────────────
  { group: 'content', key: 'website_content', label: 'Pages & News', icon: Globe,
    desc: 'Edit every WordPress page and news article directly from OL. OL is the SOT — saves publish to WordPress automatically via the bridge. No more logging into wp-admin.',
    help: 'Pages are grouped the way your site is structured (Teams, Community, Members…) — search by name or URL, click any row to edit title + body + slug + status. Save publishes to WP via the bridge automatically. News posts are dated articles on the rolling /news feed and live under the separate "Post news" card — news saves reach WP within seconds and wp-admin news edits appear in OL automatically. For pages, use Refresh from WP if you\'ve edited something directly in wp-admin.' },
  { group: 'content', key: 'team_photos', label: 'Team Photos & Hero', icon: Camera,
    desc: 'Home page carousel and team photos — the same screen AD and Scheduler use under Media.',
    help: 'Pictures are stored by OL and published to the website automatically. Team photos follow the active season of each sport (the current season once it has a schedule, else last season).' },
  { group: 'content', key: 'website_menus', label: 'Menus', icon: LinkIcon,
    desc: 'Manage WordPress header + footer navigation menus and their items end-to-end from OmniLeagues. Changes publish to WP via the bridge instantly.',
    help: 'WordPress nav menus (the header bar, footer column links, mobile drawer). Create a menu, add items with custom URLs or links to existing pages, drag-set order. Used by the theme to render the global navigation.' },
  { group: 'content', key: 'taxonomies', label: 'Categories & Tags', icon: LinkIcon,
    desc: 'Manage WP post categories and tags (used by the News tab). Create, rename, re-slug, delete — all without opening wp-admin.',
    help: 'Categories = the broad buckets a News post belongs to ("Announcements", "Game Recaps"). Tags = the fine-grained labels ("varsity", "playoffs", "tryouts"). Both feed the post archive pages and the related-posts widget.' },
  { group: 'content', key: 'comments', label: 'Comments', icon: MonitorCheck,
    desc: 'Moderate WordPress comments. Approve, hold, spam, trash and purge — keeps the AD out of wp-admin for community-moderation chores.',
    help: 'Pending comments wait here for approval before showing on the public site. Spam-flagged comments are quarantined. Trash holds deleted ones for 30 days before WP auto-purges them; the Delete-forever button skips that wait.' },

  // ── APPEARANCE ────────────────────────────────────────────────────
  { group: 'appearance', key: 'website', label: 'Homepage Feeds', icon: MonitorCheck,
    desc: 'The auto-rendered homepage feeds (ticker, sponsors, hero slider, stats, footer, nav). The WP theme polls OL for these every minute and re-renders.',
    help: 'These are NOT the page bodies (those live in Pages & News). These are the dynamic strips and blocks that wrap the page — the scrolling score ticker at the top, the sponsor carousel, the homepage hero slider, the global footer. Each section has its own editor with live preview hints.' },
  { group: 'appearance', key: 'sport_landings', label: 'Sport Landings', icon: MonitorCheck,
    desc: 'Edit the per-sport landing pages (baseball, basketball, volleyball). The WP theme reads these from the same OL feed that drives the homepage.',
    help: 'The dedicated landing for each sport (/baseball, /basketball, /volleyball). Edit the hero image, intro paragraph, program highlights bullet list, coach callouts with quotes, schedule CTA, and visibility toggle per sport.' },
  { group: 'appearance', key: 'site_config', label: 'Site Identity', icon: Globe,
    desc: 'WordPress site title, tagline, admin email, timezone, date/time format, front page selection, posts-per-page, logo and favicon URLs, plus the Customizer "Additional CSS" textarea.',
    help: 'The WordPress-level identity (what you\'d normally edit under wp-admin → Settings → General + Appearance → Customize). Sparse-PATCH means leaving a field blank means "don\'t change" — the existing WP value stays.' },
  { group: 'appearance', key: 'conversion_redirects', label: 'Payments', icon: Send,
    desc: 'Manage the payment buttons shown on your public /payments page.',
    help: 'Each enabled button with a URL appears on the /payments page. OmniLeagues is the source of truth — changes publish to the live site automatically. An enabled button with no URL will not appear.' },
  { group: 'appearance', key: 'website_visibility', label: 'Website Visibility', icon: Eye,
    desc: 'Choose what the public sees vs. what is reserved for logged-in members — pages, homepage blocks, and functions like the game-video links. Also hosts the Social Media tools.',
    help: 'Each surface can be set to Public (everyone) or Members only (logged-in members). OmniLeagues stores the choice as the source of truth and the WordPress theme obeys it on the next render. Whole pages appear here once a WordPress site is connected.' },
];

/* ─── Website Manager launcher ────────────────────────────────────────────────
   Task-first home screen. Instead of 15 flat tabs, the operator picks what
   they want to DO in plain English. Each card maps to one existing panel. */
const LAUNCHER_CARDS = [
  // key, panel section, plain-English action, sub-line, icon, count key
  { key: 'homepage', section: 'website', icon: LayoutTemplate, accent: 'text-fuchsia-500 bg-fuchsia-500/10',
    title: 'Site sections & content blocks', sub: 'Leadership & Staff, About page, Donations (QR), Footer, Members page, Records, sponsors, homepage hero & score ticker, stats, Sabres TV, team permissions.' },
  { key: 'popups', action: 'popup_modal', icon: Bell, accent: 'text-emerald-500 bg-emerald-500/10',
    title: 'Popup notifications', sub: 'Bulletin notices on the sponsor bar — registrations, store openings, announcements. Add, edit, reorder and schedule each notice.' },
  { key: 'pages', section: 'website_content', contentTab: 'pages', icon: FileText, accent: 'text-indigo-500 bg-indigo-500/10',
    title: 'Edit a page', sub: 'Change any page on the site — About, Schedules, Teams, Community…', countKey: 'pages', countLabel: 'pages' },
  { key: 'news', section: 'website_content', contentTab: 'posts', icon: Newspaper, accent: 'text-amber-500 bg-amber-500/10',
    title: 'Post news', sub: 'Write a news article — it appears in the /news feed immediately.', countKey: 'posts', countLabel: 'news posts' },
  { key: 'team_photos', section: 'team_photos', icon: Camera, accent: 'text-teal-500 bg-teal-500/10',
    title: 'Team photos & home carousel', sub: 'Upload team photos per season and the home page pictures.' },
  { key: 'menus', section: 'website_menus', icon: MenuIcon, accent: 'text-sky-500 bg-sky-500/10',
    title: 'Navigation menus', sub: 'The header bar and footer links visitors use to get around.' },
  { key: 'sports', section: 'sport_landings', icon: Trophy, accent: 'text-orange-500 bg-orange-500/10',
    title: 'Sport pages', sub: 'The /basketball, /baseball and /volleyball landing pages.' },
  { key: 'identity', section: 'site_config', icon: BadgeCheck, accent: 'text-emerald-500 bg-emerald-500/10',
    title: 'Site identity', sub: 'Site title, tagline, logo, favicon, timezone, custom CSS.' },
  { key: 'tags', section: 'taxonomies', icon: Tags, accent: 'text-violet-500 bg-violet-500/10',
    title: 'Categories & tags', sub: 'The buckets and labels that organise news posts.' },
  { key: 'comments', section: 'comments', icon: MessageSquare, accent: 'text-rose-500 bg-rose-500/10',
    title: 'Moderate comments', sub: 'Approve, hold or spam visitor comments.' },
  { key: 'redirects', section: 'conversion_redirects', icon: CornerDownRight, accent: 'text-cyan-500 bg-cyan-500/10',
    title: 'Payments', sub: 'Manage the payment buttons on your public /payments page.' },
  { key: 'visibility', section: 'website_visibility', icon: Eye, accent: 'text-teal-500 bg-teal-500/10',
    title: 'Website visibility', sub: 'Public vs. members-only for pages, blocks, and functions. Includes Social Media tools.' },
];

const PLUMBING_ROWS = [
  { section: 'linking', icon: LinkIcon, title: 'Site linking', sub: 'WordPress connection health — fix this first if anything is red.' },
  { section: 'triggers', icon: Send, title: 'Publish triggers', sub: 'When OL pushes changes to the site: live, batched or manual.' },
  { section: 'sync', icon: Activity, title: 'Sync queue', sub: 'Pending and failed OL → WordPress writes.' },
  { section: 'api', icon: Globe, title: 'API interfaces', sub: 'Endpoint inventory for integrations & exports.' },
  { section: 'player_merge', icon: Users, title: 'Player merge', sub: 'Resolve same-name player records across seasons.' },
  { section: 'staff_roles', icon: Users, title: 'Staff roles', sub: 'Roles & org areas that power Staff Positions and their slugs.' },
  { section: 'org_contact', icon: Globe, title: 'Org contact & web', sub: 'Public email, phone, social links the site renders.' },
];

export default function TechSettingsPanel({ orgId, organizations = [] }) {
  const { isDark } = useTheme();
  const [active, setActive] = useState('home');
  const [popupOpen, setPopupOpen] = useState(false);
  const [contentTab, setContentTab] = useState('pages');
  const [overview, setOverview] = useState(null);
  const [flushing, setFlushing] = useState(false);
  const [resetting, setResetting] = useState(false);

  const resetAllCaches = async () => {
    setResetting(true);
    try {
      const { data } = await axios.post(`${API}/api/wp-cache/${orgId}/reset-all`, {}, { withCredentials: true });
      const L = data?.layers || {};
      const ok = (x) => (x && x.ok ? '✓' : '✗');
      const parts = [
        `Transients ${ok(L.wp_transients)}`,
        `Deep/opcache ${ok(L.wp_deep)}`,
        `OL cache ${ok(L.ol_read_cache)}`,
        `Feed ${ok(L.ol_feed_version)}`,
      ];
      if (data?.ok) {
        toast.success(`Website cache fully reset — ${parts.join(' · ')}. Reload the site to see the latest changes.`);
      } else {
        toast.warning(`Cache reset ran with issues — ${parts.join(' · ')}.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || `Cache reset failed: ${e.message}`);
    } finally {
      setResetting(false);
    }
  };

  const flushSiteCache = async () => {
    setFlushing(true);
    try {
      const { data } = await axios.post(`${API}/api/wp-cache/${orgId}/flush`, {}, { withCredentials: true });
      const rows = data?.data?.transients_deleted;
      const entries = Number.isFinite(rows) ? Math.ceil(rows / 2) : null;
      toast.success(
        entries !== null
          ? `Website cache cleared (${entries} cache ${entries === 1 ? 'entry' : 'entries'}) — your latest changes are now live on the site.`
          : 'Website cache cleared — your latest changes are now live on the site.'
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || `Cache flush failed: ${e.message}`);
    } finally {
      setFlushing(false);
    }
  };

  // Refetch on every section change so updates made inside Site Linking
  // (e.g., the WordPress site URL/Secret) immediately propagate to the outer
  // Website Manager card — otherwise "Open live site" stays bound to the
  // value loaded at mount. Endpoint is a cheap Mongo-only read.
  useEffect(() => {
    if (!orgId) return;
    axios.get(`${API}/api/website-overview/${orgId}`, { withCredentials: true })
      .then(({ data }) => setOverview(data))
      .catch(() => {});
  }, [orgId, active]);

  const card = isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200';
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const counts = overview?.counts || {};
  const bridge = overview?.bridge || {};
  const connected = bridge.status === 'connected';

  const openCard = (c) => {
    if (c.action === 'popup_modal') { setPopupOpen(true); return; }
    if (c.contentTab) setContentTab(c.contentTab);
    setActive(c.section);
  };

  const activeSection = SECTIONS.find((s) => s.key === active);

  return (
    <div className="space-y-4" data-testid="tech-settings-panel">
      <Card className={card}>
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-teal-500/15">
                <ServerCog className="h-5 w-5 text-teal-400" />
              </div>
              <div>
                <CardTitle>Website Manager</CardTitle>
                <p className={`text-sm ${muted} mt-0.5`}>
                  Everything on the public website is controlled from here — OmniLeagues is the
                  source of truth; changes publish to WordPress automatically.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`text-xs px-2.5 py-1 rounded-full border font-medium ${
                  connected
                    ? 'bg-green-500/10 text-green-600 border-green-400/40'
                    : 'bg-red-500/10 text-red-500 border-red-400/40'
                }`}
                data-testid="wm-bridge-pill"
              >
                {connected ? `● Connected — ${bridge.site_name || 'WordPress'}` : '● Site not connected'}
              </span>
              {bridge.site_url && (
                <a href={bridge.site_url} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline" data-testid="wm-open-live-site">
                    <ExternalLink className="h-3 w-3 mr-1" /> Open live site
                  </Button>
                </a>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={flushSiteCache}
                disabled={flushing || !connected}
                title="Deletes the website's cached blocks so your latest OL changes show immediately"
                data-testid="wm-clear-site-cache"
              >
                {flushing
                  ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  : <RefreshCw className="h-3 w-3 mr-1" />}
                Clear site cache
              </Button>
              <Button
                size="sm"
                onClick={resetAllCaches}
                disabled={resetting || !connected}
                className="bg-teal-600 hover:bg-teal-700 text-white"
                title="Master reset — clears every cache layer (transients, WP-Optimize, full-page cache, PHP opcache) so PROD shows your latest changes right away"
                data-testid="wm-reset-all-cache"
              >
                {resetting
                  ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  : <RefreshCw className="h-3 w-3 mr-1" />}
                Reset Website Cache
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {active === 'home' ? (
            <div className="space-y-6" data-testid="wm-launcher">
              {/* Quick stats strip */}
              <div className={`text-xs ${muted}`} data-testid="wm-counts-strip">
                {counts.pages ?? '—'} pages · {counts.posts ?? '—'} news posts · {counts.media ?? '—'} media
                files · {counts.sponsors ?? '—'} sponsors
              </div>

              {/* Task cards */}
              <div>
                <div className={`text-[10px] uppercase tracking-wider font-semibold mb-2 ${muted}`}>
                  What do you want to do?
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {LAUNCHER_CARDS.map((c) => (
                    <button
                      key={c.key}
                      onClick={() => openCard(c)}
                      data-testid={`wm-card-${c.key}`}
                      className={`text-left p-4 rounded-xl border transition-colors group ${
                        isDark
                          ? 'bg-slate-900/40 border-slate-700 hover:border-teal-500/60 hover:bg-slate-900/70'
                          : 'bg-white border-slate-200 hover:border-teal-400 hover:bg-teal-50/40'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={`p-2 rounded-lg ${c.accent}`}>
                          <c.icon className="h-4 w-4" />
                        </span>
                        {c.countKey && counts[c.countKey] != null && (
                          <span className={`text-[11px] px-2 py-0.5 rounded-full ${
                            isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {counts[c.countKey]} {c.countLabel}
                          </span>
                        )}
                      </div>
                      <div className="font-semibold text-sm flex items-center gap-1.5">
                        {c.title}
                        <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <div className={`text-xs mt-1 leading-relaxed ${muted}`}>{c.sub}</div>
                    </button>
                  ))}
                </div>
              </div>

              <PopupNotificationsManager orgId={orgId} open={popupOpen} onOpenChange={setPopupOpen} />

              {/* Plumbing rows */}
              <div>
                <div className={`text-[10px] uppercase tracking-wider font-semibold mb-2 ${muted}`}>
                  Connection & plumbing
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {PLUMBING_ROWS.map((r) => (
                    <button
                      key={r.section}
                      onClick={() => setActive(r.section)}
                      data-testid={`wm-row-${r.section}`}
                      className={`text-left px-3 py-2.5 rounded-lg border flex items-center gap-3 transition-colors ${
                        isDark
                          ? 'bg-slate-900/30 border-slate-700/70 hover:border-slate-500'
                          : 'bg-slate-50 border-slate-200 hover:border-slate-400'
                      }`}
                    >
                      <r.icon className={`h-4 w-4 shrink-0 ${muted}`} />
                      <div className="min-w-0">
                        <div className="text-sm font-medium">{r.title}</div>
                        <div className={`text-[11px] truncate ${muted}`}>{r.sub}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Back to launcher */}
              <Button
                size="sm"
                variant="ghost"
                className="mb-3 -ml-2"
                onClick={() => setActive('home')}
                data-testid="wm-back-home"
              >
                <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Website Manager
              </Button>

              {/* Active-section help block — one-sentence summary + paragraph */}
              {activeSection && (
                <div className={`mb-4 p-3 rounded border ${
                  isDark
                    ? 'bg-teal-950/30 border-teal-800/40 text-slate-300'
                    : 'bg-teal-50 border-teal-200 text-slate-700'
                }`} data-testid="tech-settings-help">
                  <div className="font-medium text-sm mb-1 flex items-center gap-2">
                    <activeSection.icon className="h-3.5 w-3.5 text-teal-500" />
                    {activeSection.label}
                  </div>
                  <div className={`text-xs ${muted} mb-2`}>{activeSection.desc}</div>
                  {activeSection.help && (
                    <div className={`text-xs leading-relaxed ${
                      isDark ? 'text-slate-400' : 'text-slate-600'
                    }`}>{activeSection.help}</div>
                  )}
                </div>
              )}

              {active === 'linking' && <SiteLinkingSection orgId={orgId} organizations={organizations} />}
              {active === 'org_contact' && <OrgProfilePanel orgId={orgId} isDark={isDark} showIdentity={false} showContact={true} />}
              {active === 'api' && <ApiInterfacesPanel orgId={orgId} />}
              {active === 'triggers' && <><PublishTriggersSection orgId={orgId} isDark={isDark} /><GamesPublishFlagSection orgId={orgId} isDark={isDark} /><WpMgmtDefaultsSection orgId={orgId} isDark={isDark} /></>}
              {active === 'sync' && <SyncQueueSection orgId={orgId} />}
              {active === 'player_merge' && <PlayerMergeSection orgId={orgId} />}
              {active === 'staff_roles' && <StaffRolesPanel orgId={orgId} />}
              {active === 'website' && <WebsiteSettingsSection orgId={orgId} organizations={organizations} />}
              {active === 'website_content' && <WebsiteContentPanel orgId={orgId} kind={contentTab} />}
              {active === 'team_photos' && (
                <div className="space-y-6">
                  <div><div className="text-sm font-semibold mb-2">Home page carousel</div><HeroCarouselManager orgId={orgId} isDark={isDark} /></div>
                  <div><div className="text-sm font-semibold mb-2">Team photos</div><TeamPhotosManager orgId={orgId} isDark={isDark} /></div>
                </div>
              )}
              {active === 'website_menus' && <WebsiteMenusPanel orgId={orgId} />}
              {active === 'sport_landings' && <SportLandingsPanel orgId={orgId} />}
              {active === 'conversion_redirects' && <ConversionRedirectsPanel orgId={orgId} />}
              {active === 'website_visibility' && <WebsiteVisibilityPanel orgId={orgId} />}
              {active === 'site_config' && <SiteConfigPanel orgId={orgId} />}
              {active === 'taxonomies' && <TaxonomiesPanel orgId={orgId} />}
              {active === 'comments' && <CommentsModerationPanel orgId={orgId} />}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ─── WP Bridge subsection ────────────────────────────────────────────────── */

function BridgeSection({ orgId, onConnectionChange }) {
  const { isDark } = useTheme();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [siteUrl, setSiteUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/bridge-proxy/config/${orgId}`);
      if (data.ok && data.data) {
        setCfg(data.data);
        setSiteUrl(data.data.site_url || '');
      } else {
        setCfg(null);
      }
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (orgId) load();
  }, [orgId, load]);

  const handleSave = async () => {
    if (!siteUrl || !apiKey) {
      setErr('Both site URL and API key are required.');
      return;
    }
    setSaving(true);
    setErr(null);
    setMsg(null);
    try {
      const { data } = await axios.post(`${API}/api/bridge-proxy/config/${orgId}`, {
        site_url: siteUrl.trim().replace(/\/+$/, ''),
        api_key: apiKey,
      });
      if (data.ok && data.connected) {
        setCfg(data.data);
        setApiKey('');
        setMsg('Connected — health check passed.');
        onConnectionChange?.();
      } else {
        setErr(data.error?.message || 'Connection failed. Verify the URL and key.');
      }
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setErr(null);
    setMsg(null);
    try {
      const { data } = await axios.post(`${API}/api/bridge-proxy/config/${orgId}/test`);
      if (data.ok) {
        setMsg('Connection OK.');
        load();
      } else {
        setErr(data.error?.message || 'Test failed.');
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setTesting(false);
    }
  };

  /** Reveal the current plaintext OL Secret Key from the backend and prefill
   *  the editor so the TS can SEE the secret. Required because the masked
   *  preview alone doesn't let an operator verify what's currently
   *  configured against what's pasted into WP. */
  const handleReveal = async () => {
    setRevealing(true);
    setErr(null);
    try {
      const { data } = await axios.get(
        `${API}/api/bridge-proxy/config/${orgId}/secret`,
        { withCredentials: true },
      );
      if (data?.ok && data?.api_key) {
        setApiKey(data.api_key);
        setShowSecret(true);
        setMsg('Secret revealed — it is now visible and editable below.');
        setTimeout(() => setMsg(null), 3000);
      } else {
        setErr('No secret on file for this org.');
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      setErr(detail || e.message);
    } finally {
      setRevealing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect this WordPress site? Data on WP is unchanged.')) return;
    await axios.delete(`${API}/api/bridge-proxy/config/${orgId}`);
    setCfg(null);
    setSiteUrl('');
    setApiKey('');
    onConnectionChange?.();
  };

  const connected = cfg?.status === 'connected';
  const health = cfg?.health_data;
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-10 justify-center">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Status card */}
      {connected && health && (
        <Card className="bg-emerald-900/20 border-emerald-700/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />
                <span className="font-semibold text-emerald-300 text-sm truncate">
                  {health.site_name}
                </span>
                <span className="text-xs text-slate-500">Bridge v{health.bridge_version}</span>
              </div>
              <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                Connected
              </Badge>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
              {[
                { l: 'Sports', v: health.sports_configured },
                { l: 'Teams', v: health.teams_count },
                { l: 'Players', v: health.players_count },
                { l: 'Games', v: health.games_count },
              ].map((s) => (
                <div key={s.l} className="text-center p-2 bg-slate-800/50 rounded">
                  <p className="text-base font-bold text-slate-100">{s.v}</p>
                  <p className="text-[10px] text-slate-400 uppercase">{s.l}</p>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-slate-400 space-y-0.5">
              <div>Site URL: <span className="text-slate-200">{cfg?.site_url}</span></div>
              <div>OL Secret Key: <span className="font-mono">{cfg?.api_key_preview}</span></div>
              <div>Timezone: <span className="text-slate-200">{cfg?.settings?.timezone || 'America/New_York'}</span></div>
              <div>WP {health.wp_version} · PHP {health.php_version} · MySQL {health.mysql_version}</div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" onClick={handleTest} disabled={testing} data-testid="bridge-test-btn">
                {testing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                Test Connection
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-red-800/50 text-red-400"
                onClick={handleDisconnect}
                data-testid="bridge-disconnect-btn"
              >
                <Trash2 className="h-3 w-3 mr-1" /> Disconnect
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit form (always shown so TS can rotate URL/key) */}
      <Card className={isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200'}>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-amber-400" />
            {connected ? 'Update Connection' : 'Connect WordPress'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>WordPress Site URL</Label>
            <Input
              value={siteUrl}
              onChange={(e) => setSiteUrl(e.target.value)}
              placeholder="https://southwakesabres.org"
              data-testid="bridge-site-url"
            />
            <p className={`text-[11px] ${muted} mt-1`}>Canonical URL of the WP site (no trailing slash).</p>
          </div>
          <div>
            <Label>OL Secret Key {connected && <span className={`text-[11px] ${muted}`}>(leave blank to keep current)</span>}</Label>
            <div className="relative">
              <Input
                type={showSecret ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={connected ? '•••• (unchanged) — click Show to reveal' : 'From WP Admin → Settings → OmniLeagues → OL Secret Key'}
                className="pr-10"
                data-testid="bridge-api-key"
              />
              <button
                type="button"
                onClick={() => setShowSecret((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                tabIndex={-1}
                aria-label="Toggle secret visibility"
              >
                {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              {connected && (
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={handleReveal}
                  disabled={revealing}
                  className="border-amber-700/60 text-amber-300 hover:bg-amber-900/30 h-7 text-[11px]"
                  data-testid="bridge-reveal-secret-btn"
                >
                  {revealing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
                  Show current key
                </Button>
              )}
              <p className={`text-[11px] ${muted} flex-1`}>
                The shared secret OL and WordPress use to authenticate each
                other. Same value goes into <strong>WP Admin → Settings → OmniLeagues → OL Secret Key</strong>.
              </p>
            </div>
          </div>
          {err && (
            <div className="text-sm text-red-400 bg-red-900/20 p-2 rounded flex items-center gap-1">
              <AlertTriangle className="h-4 w-4" /> {err}
            </div>
          )}
          {msg && (
            <div className="text-sm text-emerald-400 bg-emerald-900/20 p-2 rounded">{msg}</div>
          )}
          <div className="flex gap-2">
            <Button
              onClick={handleSave}
              disabled={!siteUrl || (!apiKey && !connected) || saving}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="bridge-save-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ExternalLink className="h-4 w-4 mr-2" />}
              {connected ? 'Save Changes' : 'Connect'}
            </Button>
            {connected && (
              <Button variant="outline" onClick={load} data-testid="bridge-refresh-btn">
                <RefreshCw className="h-4 w-4 mr-1" /> Refresh
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ─── Publish Triggers is now imported from AdminSettingsPanel ───────────── */

/* ─── Site Linking subsection ─────────────────────────────────────────────
   Multi-tenant linking surface (Phase 5, shipped May 11 2026).
   Shows the Tech Specialist their OL Org UUID and the exact 4-step
   procedure for pasting it into the OL Bridge WP plugin's settings page.
   Plugin contract spec: /app/docs/WP_PLUGIN_MULTI_TENANT_BRIEF.md
*/
function SiteLinkingSection({ orgId, organizations = [] }) {
  const { isDark } = useTheme();
  const [copied, setCopied] = useState(false);
  const [bridgeCfg, setBridgeCfg] = useState(null);
  const [versionInfo, setVersionInfo] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [bridgeRefreshKey, setBridgeRefreshKey] = useState(0);

  const card = isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200';
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const orgName = (organizations.find((o) => o.id === orgId) || {}).name || '';

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/bridge-proxy/config/${orgId}`);
        if (data?.ok && data?.data) {
          setBridgeCfg(data.data);
        }
      } catch (_) { /* no config yet */ }
      try {
        const { data } = await axios.get(`${API}/api/website-settings/${orgId}/version`);
        setVersionInfo(data);
      } catch (_) { /* feed not initialised */ }
    })();
  }, [orgId]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(orgId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      // older browsers — fallback to manual select
      const el = document.createElement('textarea');
      el.value = orgId; document.body.appendChild(el);
      el.select(); document.execCommand('copy'); document.body.removeChild(el);
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    }
  };

  const reloadStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/api/bridge-proxy/config/${orgId}`);
      setBridgeCfg(data?.ok && data?.data ? data.data : null);
    } catch (_) { /* no config */ }
    try {
      const { data } = await axios.get(`${API}/api/website-settings/${orgId}/version`);
      setVersionInfo(data);
    } catch (_) { /* no feed yet */ }
  };

  return (
    <div className="space-y-4" data-testid="site-linking-section">
      {/* Wizard launcher — always visible at the top */}
      <Card className={`bg-gradient-to-br from-teal-900/40 via-emerald-900/30 to-slate-900/60 border-teal-600/40`}>
        <CardContent className="p-4">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="p-2 rounded-lg bg-teal-500/20 flex-shrink-0">
              <Sparkles className="h-5 w-5 text-teal-300" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <h3 className="text-sm font-semibold text-teal-100">Quick Onboarding Wizard</h3>
              <p className="text-[12px] text-teal-200/80 mt-0.5">
                Four steps · ~2 minutes. We'll walk you through linking a WordPress site to this OL
                organisation: confirm your UUID, save the WP Bridge connection, copy the three plugin
                values, and verify the link end-to-end.
              </p>
            </div>
            <Button
              onClick={() => setWizardOpen(true)}
              className="bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold"
              data-testid="site-linking-launch-wizard-btn"
            >
              <Sparkles className="h-4 w-4 mr-1.5" />
              Start Wizard
            </Button>
          </div>
        </CardContent>
      </Card>

      <QuickOnboardingWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        orgId={orgId}
        orgName={orgName}
        initialBridgeCfg={bridgeCfg}
        onComplete={() => { reloadStatus(); setBridgeRefreshKey(k => k + 1); }}
      />

      {/* WordPress Bridge — live connection health, URL/Secret rotation,
          disconnect. Merged here in Session 19 (May 12 2026) so the Tech
          Specialist has a single Site Linking surface instead of two
          near-identical Bridge + Linking tabs. */}
      <BridgeSection
        orgId={orgId}
        key={`bridge-${bridgeRefreshKey}`}
        onConnectionChange={reloadStatus}
      />

      {/* Identity card — the Org UUID */}
      <Card className={`${card} border-emerald-700/50`}>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <LinkIcon className="h-4 w-4 text-emerald-400" />
            Your OmniLeagues Org UUID
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className={`text-[12px] ${muted}`}>
            This is the unique identifier the WordPress plugin needs to pull
            content for <strong className={isDark ? 'text-slate-100' : 'text-slate-800'}>{orgName || 'this organisation'}</strong>.
            Paste it into <em>WP Admin → Settings → OmniLeagues → Org UUID</em> on the matching
            WordPress site.
          </p>
          <div className="flex items-stretch gap-2">
            <code
              className="flex-1 px-3 py-2 rounded-md bg-slate-900/70 border border-slate-700 text-emerald-300 font-mono text-xs break-all"
              data-testid="site-linking-org-uuid"
            >
              {orgId || '—'}
            </code>
            <Button
              onClick={handleCopy}
              disabled={!orgId}
              className={copied ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-teal-600 hover:bg-teal-700'}
              data-testid="site-linking-copy-uuid-btn"
            >
              {copied ? <CheckCircle2 className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          {versionInfo && (
            <div className={`text-[11px] ${muted} flex items-center gap-3 flex-wrap`}>
              <span>Public feed version: <span className="text-emerald-400 font-semibold">v{versionInfo.version ?? '—'}</span></span>
              {versionInfo.updated_at && (
                <span>Last saved: {new Date(versionInfo.updated_at).toLocaleString()}</span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Verify Link card removed in Session 19 (May 12 2026) — the
          WordPress Site URL field is already shown above in the Update
          Connection form, and a healthy connection card at the top of
          this page is the definitive "is this linked?" signal. Having
          the same WP URL field twice on one page was confusing. */}

      {/* Install instructions */}
      <Card className={card}>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <ExternalLink className="h-4 w-4 text-teal-400" />
            WP Plugin Install Steps
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ol className={`text-[12px] ${muted} space-y-2 list-decimal pl-5`}>
            <li>
              Install or update the <strong>OmniLeagues Bridge</strong> plugin on the target
              WordPress site (one zip works for every OL org).
            </li>
            <li>
              In WP Admin, open <em>Settings → OmniLeagues</em>.
            </li>
            <li>
              Paste the <strong>Org UUID</strong> shown above into the <em>OL Org UUID</em>
              field, paste the <strong>OL Secret Key</strong> into the <em>OL Secret Key</em>
              field, then click <strong>Test Connection</strong>. A green check means the
              plugin successfully resolved this OL org and pulled the public feed.
            </li>
            <li>
              Back in OL, open the <strong>Website</strong> subtab and start editing — the WP
              site will pick up changes on the next polling cycle (~60s).
            </li>
          </ol>
          <div className={`mt-3 text-[11px] ${muted} bg-slate-900/40 border border-slate-700/60 rounded p-2`}>
            <strong className="text-slate-200">For the plugin maintainer:</strong> the exact
            settings-page contract is documented in <code className="text-amber-300">/app/docs/WP_PLUGIN_MULTI_TENANT_BRIEF.md</code>{' '}
            (also surfaced via <code className="text-amber-300">/api/public/resolve-org</code>).
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


/* ─── Quick Onboarding Wizard ─────────────────────────────────────────────
   4-step guided onboarding for a Tech Specialist linking a new WordPress
   site to an OL organisation. Shipped Session 18 (May 11, 2026) — closes
   the discoverability gap on the multi-tenant flow with a single
   "happy path" UI.

     Step 1 — Identity:  show the OL Org UUID, Copy.
     Step 2 — Bridge:    save WP site URL + Bridge Secret to ol_site_config.
     Step 3 — Paste:     surface the 3 plugin values (UUID, Secret, Base URL).
     Step 4 — Verify:    run /version (primary) + resolve-org (optional hint).

   Matches the plugin maintainer's brief at
   /app/docs/WP_PLUGIN_MULTI_TENANT_BRIEF.md so the wizard's exit state
   exactly mirrors the values the operator will paste into WP.
*/
function QuickOnboardingWizard({ open, onOpenChange, orgId, orgName, initialBridgeCfg, onComplete }) {
  const { isDark } = useTheme();
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';

  const [step, setStep] = useState(1);
  const TOTAL_STEPS = 4;

  // Step 2 — bridge connection state
  const [siteUrl, setSiteUrl]   = useState('');
  const [apiKey, setApiKey]     = useState('');
  const [bridgeSaving, setBridgeSaving] = useState(false);
  const [bridgeError, setBridgeError]   = useState(null);
  const [bridgeSaved, setBridgeSaved]   = useState(false);
  const [showSecret, setShowSecret]     = useState(false);

  // Step 4 — verification state
  const [primaryCheck, setPrimaryCheck]   = useState(null);  // {ok, version, updated_at, msg}
  const [secondaryCheck, setSecondaryCheck] = useState(null); // {tone:'ok'|'info'|'warn', msg}
  const [verifying, setVerifying] = useState(false);

  const OL_BASE_URL = (API || '').replace(/\/+$/, '');

  // Reset / hydrate when dialog opens
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setBridgeError(null);
    setBridgeSaved(false);
    setPrimaryCheck(null);
    setSecondaryCheck(null);
    if (initialBridgeCfg?.site_url) {
      setSiteUrl(initialBridgeCfg.site_url);
      // Existing connection → consider step 2 already satisfied
      setBridgeSaved(true);
    } else {
      setSiteUrl('');
    }
    setApiKey('');
  }, [open, initialBridgeCfg]);

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (_) {
      const el = document.createElement('textarea');
      el.value = text; document.body.appendChild(el);
      el.select(); document.execCommand('copy'); document.body.removeChild(el);
    }
    // Lightweight feedback: flash a small console hint; the per-row button
    // re-render after click is the primary signal users see.
    // eslint-disable-next-line no-console
    console.debug(`[OnboardingWizard] copied ${label}`);
  };

  const handleConnectBridge = async () => {
    setBridgeError(null);
    setBridgeSaving(true);
    try {
      const { data } = await axios.post(`${API}/api/bridge-proxy/config/${orgId}`, {
        site_url: siteUrl.trim().replace(/\/+$/, ''),
        api_key: apiKey,
      });
      if (data?.ok && data?.connected) {
        setBridgeSaved(true);
        setStep(3);
      } else {
        setBridgeError(data?.error?.message || 'Connection failed. Verify the URL and Secret.');
      }
    } catch (e) {
      setBridgeError(e.response?.data?.detail || e.message);
    } finally {
      setBridgeSaving(false);
    }
  };

  const runVerification = async () => {
    setVerifying(true);
    setPrimaryCheck(null);
    setSecondaryCheck(null);

    // Primary — /version (UUID validator)
    try {
      const { data } = await axios.get(`${API}/api/website-settings/${orgId}/version`);
      setPrimaryCheck({
        ok: true,
        version: data?.version,
        updated_at: data?.updated_at,
        msg: `Linked to OL — feed v${data?.version ?? '?'}${data?.updated_at ? `, last updated ${new Date(data.updated_at).toLocaleString()}` : ''}.`,
      });
    } catch (e) {
      setPrimaryCheck({
        ok: false,
        msg: e.response?.status === 404
          ? 'OL has no organisation with this UUID. Refresh the page and re-copy the UUID from step 1.'
          : (e.response?.data?.detail || e.message),
      });
      setVerifying(false);
      return;
    }

    // Secondary — resolve-org (optional hint, NEVER a hard error)
    try {
      const { data } = await axios.get(`${API}/api/public/resolve-org`, { params: { wp_url: siteUrl } });
      if (data?.org_id === orgId) {
        setSecondaryCheck({ tone: 'ok',
          msg: 'OL has this WP URL registered. Inbound writes (score push, roster push) will route correctly.' });
      } else {
        setSecondaryCheck({ tone: 'warn',
          msg: `OL has that URL registered to a different OL org${data?.name ? ` ("${data.name}")` : ''}. Read-only content still works — only inbound writes are affected.` });
      }
    } catch (e) {
      if (e.response?.status === 404) {
        setSecondaryCheck({ tone: 'info',
          msg: 'OL hasn\'t registered this WP URL yet. That\'s optional — read-only content (ticker, hero, sponsors, etc.) works fine without it. Only needed if you want inbound writes flowing from WP back to OL.' });
      } else {
        setSecondaryCheck({ tone: 'info', msg: `Couldn't run optional URL check (${e.message}). Not a problem — primary link is good.` });
      }
    } finally {
      setVerifying(false);
    }
  };

  // Navigation guards
  const canAdvance = (() => {
    if (step === 1) return !!orgId;
    if (step === 2) return bridgeSaved;
    if (step === 3) return true;
    return false;
  })();

  const handleFinish = async () => {
    if (onComplete) await onComplete();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl bg-slate-900 border-slate-700 text-slate-100"
        data-testid="quick-onboarding-wizard"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-teal-300" />
            Quick Onboarding — Step {step} of {TOTAL_STEPS}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {step === 1 && 'Confirm the OL identity that the WordPress site will render.'}
            {step === 2 && 'Save the WP Bridge connection so OL can talk to your WordPress install.'}
            {step === 3 && 'Three values to paste into WP Admin → Settings → OmniLeagues.'}
            {step === 4 && 'Verify the link is live and ready for content edits.'}
          </DialogDescription>
        </DialogHeader>

        <Progress value={(step / TOTAL_STEPS) * 100} className="h-1.5 bg-slate-800" />

        <div className="py-2 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* ── Step 1 — Identity ────────────────────────────────────────── */}
          {step === 1 && (
            <div className="space-y-3" data-testid="wizard-step-1">
              <div className="p-3 rounded-lg bg-emerald-900/20 border border-emerald-700/40">
                <p className="text-[11px] uppercase tracking-wide text-emerald-300/80 mb-1 font-semibold">
                  Organisation
                </p>
                <p className="text-base font-semibold">{orgName || '—'}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-300 mb-1.5 block">OL Org UUID (read-only)</Label>
                <div className="flex items-stretch gap-2">
                  <code className="flex-1 px-3 py-2 rounded-md bg-slate-950 border border-slate-700 text-emerald-300 font-mono text-xs break-all">
                    {orgId || '—'}
                  </code>
                  <Button
                    onClick={() => copyText(orgId, 'UUID')}
                    className="bg-teal-600 hover:bg-teal-500"
                    data-testid="wizard-copy-uuid"
                  >
                    <Copy className="h-4 w-4 mr-1" /> Copy
                  </Button>
                </div>
                <p className={`text-[11px] ${muted} mt-1.5`}>
                  This UUID was auto-generated when the organisation was created. It is the single
                  value that links your WordPress site to OL.
                </p>
              </div>
            </div>
          )}

          {/* ── Step 2 — Bridge connection ───────────────────────────────── */}
          {step === 2 && (
            <div className="space-y-3" data-testid="wizard-step-2">
              <p className={`text-[12px] ${muted}`}>
                Enter the public URL of the WordPress site and the Bridge Secret you'll paste into the
                plugin. OL saves this so the inbound bridge proxy (score push, roster push, media)
                can find the correct WP install.
              </p>
              <div>
                <Label className="text-xs text-slate-300 mb-1.5 block">WordPress Site URL</Label>
                <Input
                  value={siteUrl}
                  onChange={(e) => setSiteUrl(e.target.value)}
                  placeholder="https://southwakesabres.org"
                  className="bg-slate-950 border-slate-700"
                  data-testid="wizard-site-url"
                />
                <p className={`text-[11px] ${muted} mt-1`}>Canonical URL (no trailing slash).</p>
              </div>
              <div>
                <Label className="text-xs text-slate-300 mb-1.5 block">Bridge Secret (OL Secret Key)</Label>
                <div className="relative">
                  <Input
                    type={showSecret ? 'text' : 'password'}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder={bridgeSaved && !apiKey ? '•••• (existing secret kept)' : 'Generate or paste a strong secret'}
                    className="bg-slate-950 border-slate-700 pr-10"
                    data-testid="wizard-api-key"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecret((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                    tabIndex={-1}
                    aria-label="Toggle secret visibility"
                  >
                    {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className={`text-[11px] ${muted} mt-1`}>
                  Same value goes into <em>WP Admin → Settings → OmniLeagues → OL Secret Key</em>.
                </p>
              </div>
              {bridgeSaved && (
                <div className="text-[12px] text-emerald-300 bg-emerald-900/20 border border-emerald-700/40 p-2 rounded flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>Bridge connection already saved for <span className="font-mono">{siteUrl}</span>. You can click Next, or change the values above and click <strong>Save & Test Bridge</strong> to update.</span>
                </div>
              )}
              {bridgeError && (
                <div className="text-[12px] text-red-300 bg-red-900/20 border border-red-700/40 p-2 rounded flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{bridgeError}</span>
                </div>
              )}
              <Button
                onClick={handleConnectBridge}
                disabled={!siteUrl || !apiKey || bridgeSaving}
                className="bg-amber-600 hover:bg-amber-500"
                data-testid="wizard-save-bridge-btn"
              >
                {bridgeSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <KeyRound className="h-4 w-4 mr-1.5" />}
                {bridgeSaved ? 'Update & Re-Test Bridge' : 'Save & Test Bridge'}
              </Button>
            </div>
          )}

          {/* ── Step 3 — Three values to paste ───────────────────────────── */}
          {step === 3 && (
            <div className="space-y-3" data-testid="wizard-step-3">
              <p className={`text-[12px] ${muted}`}>
                Open <em>WP Admin → Settings → OmniLeagues</em> on the connected WordPress site and
                paste these three values. Then click <strong>Test Connection</strong> in WP to
                confirm the plugin sees OL.
              </p>
              <WizardCopyRow label="① OL Org UUID"   value={orgId}        onCopy={() => copyText(orgId, 'UUID')} testId="wizard-paste-uuid" />
              <WizardCopyRow label="② OL Secret Key" value={apiKey || (bridgeSaved ? '(use the existing secret on the WP install)' : '—')} onCopy={() => apiKey && copyText(apiKey, 'Secret')} testId="wizard-paste-secret" mono={!!apiKey} disabled={!apiKey} />
              <WizardCopyRow label="③ OL Base URL"   value={OL_BASE_URL}  onCopy={() => copyText(OL_BASE_URL, 'Base URL')} testId="wizard-paste-base-url" />
              <div className={`text-[11px] ${muted} bg-slate-800/60 border border-slate-700/60 p-2 rounded leading-relaxed`}>
                <strong className="text-slate-200">Note:</strong> the WP plugin already knows its own
                site URL via <code className="text-teal-300">home_url()</code> — you do <strong>not</strong>{' '}
                need to type the WP URL into the plugin settings. The plugin is intentionally three
                fields only (UUID, Secret, Base URL).
              </div>
            </div>
          )}

          {/* ── Step 4 — Verify ──────────────────────────────────────────── */}
          {step === 4 && (
            <div className="space-y-3" data-testid="wizard-step-4">
              <p className={`text-[12px] ${muted}`}>
                Click <strong>Run Verification</strong>. The primary check validates that this OL Org
                UUID resolves to a live feed. The secondary check is an optional hint about whether
                OL has your WP URL registered for inbound writes.
              </p>
              <Button
                onClick={runVerification}
                disabled={verifying}
                className="bg-teal-600 hover:bg-teal-500"
                data-testid="wizard-run-verify-btn"
              >
                {verifying ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
                Run Verification
              </Button>

              {primaryCheck && (
                <div
                  className={`text-[12px] p-2 rounded flex items-start gap-2 border ${
                    primaryCheck.ok
                      ? 'text-emerald-300 bg-emerald-900/20 border-emerald-700/40'
                      : 'text-red-300 bg-red-900/20 border-red-700/40'
                  }`}
                  data-testid="wizard-primary-result"
                >
                  {primaryCheck.ok ? <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" /> : <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />}
                  <div>
                    <p className="font-semibold mb-0.5">Primary check — UUID validator</p>
                    <p>{primaryCheck.msg}</p>
                  </div>
                </div>
              )}

              {secondaryCheck && (
                <div
                  className={`text-[12px] p-2 rounded flex items-start gap-2 border ${
                    secondaryCheck.tone === 'ok'
                      ? 'text-emerald-300 bg-emerald-900/20 border-emerald-700/40'
                      : secondaryCheck.tone === 'warn'
                        ? 'text-amber-300 bg-amber-900/20 border-amber-700/40'
                        : 'text-slate-300 bg-slate-800/50 border-slate-700'
                  }`}
                  data-testid="wizard-secondary-result"
                >
                  {secondaryCheck.tone === 'ok'
                    ? <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    : <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />}
                  <div>
                    <p className="font-semibold mb-0.5">
                      Secondary check — WP URL registration{' '}
                      <span className="font-normal text-[10px] uppercase tracking-wide opacity-70">(optional)</span>
                    </p>
                    <p>{secondaryCheck.msg}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-between items-center pt-2 border-t border-slate-800">
          <Button
            variant="outline"
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            disabled={step === 1}
            className="border-slate-700"
            data-testid="wizard-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <span className={`text-[11px] ${muted}`}>{step} / {TOTAL_STEPS}</span>
          {step < TOTAL_STEPS ? (
            <Button
              onClick={() => setStep((s) => Math.min(TOTAL_STEPS, s + 1))}
              disabled={!canAdvance}
              className="bg-teal-600 hover:bg-teal-500"
              data-testid="wizard-next-btn"
            >
              Next <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleFinish}
              className="bg-emerald-600 hover:bg-emerald-500"
              data-testid="wizard-finish-btn"
            >
              <CheckCircle2 className="h-4 w-4 mr-1" /> Finish
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* helper — single copy row for Step 3 */
function WizardCopyRow({ label, value, onCopy, testId, mono = true, disabled = false }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1 font-semibold">{label}</p>
      <div className="flex items-stretch gap-2">
        <code className={`flex-1 px-3 py-2 rounded-md bg-slate-950 border border-slate-700 ${mono ? 'font-mono' : ''} text-xs ${disabled ? 'text-slate-500 italic' : 'text-emerald-300'} break-all`}>
          {value}
        </code>
        <Button
          onClick={onCopy}
          disabled={disabled}
          className="bg-teal-600 hover:bg-teal-500"
          data-testid={testId}
        >
          <Copy className="h-4 w-4 mr-1" /> Copy
        </Button>
      </div>
    </div>
  );
}

